import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Menu,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  FileDownload as FileDownloadIcon,
  Tune as TuneIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/services/api';

// 导入排序对话框组件 - 使用相对路径
import MonitoringIndicatorSortDialog from '@/components/indicators/MonitoringIndicatorSortDialog';
// 导入导入对话框组件
import MonitoringIndicatorImportDialog from '@/components/indicators/MonitoringIndicatorImportDialog';

// 固定事中监控指标模板
const FIXED_MONITORING_INDICATORS = [
  {
    "level1_name": "资金管理",
    "level2_name": "预算执行情况",
    "value_description": "项目预算执行进度和资金使用情况，定量指标，必填"
  },
  {
    "level1_name": "资金管理", 
    "level2_name": "资金到位情况",
    "value_description": "项目资金实际到位情况，定量指标，必填"
  },
  {
    "level1_name": "资金管理",
    "level2_name": "资金使用合规性", 
    "value_description": "资金使用是否符合相关规定，定性指标，必填"
  },
  {
    "level1_name": "进度管理",
    "level2_name": "项目实施进度",
    "value_description": "项目按计划实施的进度情况，定量指标，必填"
  },
  {
    "level1_name": "进度管理",
    "level2_name": "阶段性任务完成情况", 
    "value_description": "各阶段任务完成情况，定性/定量指标，必填"
  },
  {
    "level1_name": "质量管理",
    "level2_name": "质量控制情况",
    "value_description": "项目质量标准和执行情况，定性/定量指标，必填"
  },
  {
    "level1_name": "质量管理",
    "level2_name": "问题整改情况",
    "value_description": "发现问题的整改落实情况，定性指标，必填"
  },
  {
    "level1_name": "风险管理",
    "level2_name": "风险识别与应对", 
    "value_description": "项目风险识别和应对措施情况，定性指标，必填"
  },
  {
    "level1_name": "绩效管理",
    "level2_name": "预期绩效实现情况",
    "value_description": "项目预期绩效目标的实现程度，定性/定量指标，必填"
  },
  {
    "level1_name": "绩效管理",
    "level2_name": "偏差分析",
    "value_description": "与预期目标的偏差情况及原因分析，定性/定量指标，必填"
  },
];

// 事中监控指标数据类型定义
interface MonitoringIndicator {
  id: number;
  project_id: number;
  level1_code: string;
  level1_name: string;
  level2_code: string;
  level2_name: string;
  level3_code: string;
  level3_name: string;
  indicator_value: string;
  value_description: string;
  execution_1_7: string;
  expected_full_year: string;
  deviation_reason: string;
  reason_description: string;
  completion_probability: string;
  note: string;
  ai_recognition_basis: string;
  created_at: string;
  updated_at: string;
}

interface MonitoringIndicatorFormData {
  level1_code: string;
  level1_name: string;
  level2_code: string;
  level2_name: string;
  level3_code: string;
  level3_name: string;
  indicator_value: string;
  value_description: string;
  execution_1_7: string;
  expected_full_year: string;
  deviation_reason: string;
  reason_description: string;
  completion_probability: string;
  note: string;
  ai_recognition_basis: string;
}

const MonitoringIndicatorManager: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  
  // 状态管理
  const [indicators, setIndicators] = useState<MonitoringIndicator[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState<any>(null);
  
  // 对话框状态
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<MonitoringIndicator | null>(null);
  const [deletingIndicator, setDeletingIndicator] = useState<MonitoringIndicator | null>(null);
  
  // 批量删除相关状态
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<number[]>([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  
  // 导出相关状态
  const [isExporting, setIsExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  
  // 排序相关状态
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  
  // 生成执行情况相关状态
  const [isGeneratingExecution, setIsGeneratingExecution] = useState(false);
  
  // 导入相关状态
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // AI推荐指标值相关状态
  const [recommendingValue, setRecommendingValue] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null); // 对话框专用错误状态
  
  // 项目绩效元信息（可编辑）
  const [meta, setMeta] = useState<any>({
    department_name: '',
    unit_name: '',
    annual_target: '',
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [extractingMeta, setExtractingMeta] = useState(false);
  
  // 资金执行情况列表
  interface FundExecution {
    id?: number;
    fund_type: string;
    initial_budget: string;
    adjusted_budget: string;
    execution_1_7: string;
    execution_1_7_rate: string;
    expected_full_year: string;
    expected_full_year_rate: string;
    sort_order: number;
  }
  const [fundExecutions, setFundExecutions] = useState<FundExecution[]>([]);
  
  // 表单数据
  const [formData, setFormData] = useState<MonitoringIndicatorFormData>({
    level1_code: '',
    level1_name: '',
    level2_code: '',
    level2_name: '',
    level3_code: '',
    level3_name: '',
    indicator_value: '',
    value_description: '',
    execution_1_7: '',
    expected_full_year: '',
    deviation_reason: '',
    reason_description: '',
    completion_probability: '',
    note: '',
    ai_recognition_basis: '',
  });

  // 获取项目信息
  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      
      if (response.success && response.data) {
        setProject(response.data);
      } else {
        console.error('获取项目信息失败:', response.message);
      }
    } catch (err: any) {
      console.error('获取项目信息失败:', err);
    }
  }, [projectId]);

  // 获取事中监控指标列表
  const fetchIndicators = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/projects/${projectId}/monitoring-indicators?page=1&page_size=1000`);
      
      if (response.success && response.data) {
        setIndicators(response.data);
        setError(null);
      } else {
        setError(response.message || '获取事中监控指标列表失败');
      }
    } catch (err: any) {
      console.error('获取事中监控指标列表失败:', err);
      setError(err.response?.data?.detail || '获取事中监控指标列表失败');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 获取项目绩效元信息
  const fetchProjectMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}/performance-meta`);
      if (response.success && response.data) {
        setMeta({
          department_name: response.data.department_name || '',
          unit_name: response.data.unit_name || '',
          annual_target: response.data.annual_target || '',
        });
      }
    } catch (e) {
      console.error('获取项目元信息失败', e);
    }
  }, [projectId]);

  // 获取资金执行情况
  const fetchFundExecutions = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}/fund-executions`);
      if (response.success && response.data && response.data.items) {
        // 如果数据库中没有数据，初始化默认的4行数据
        if (response.data.items.length === 0) {
          const defaultExecutions: FundExecution[] = [
            {
              fund_type: '年度资金总额',
              initial_budget: '',
              adjusted_budget: '',
              execution_1_7: '',
              execution_1_7_rate: '',
              expected_full_year: '',
              expected_full_year_rate: '',
              sort_order: 0,
            },
            {
              fund_type: '其中:财政拨款(政府预算资金)',
              initial_budget: '',
              adjusted_budget: '',
              execution_1_7: '',
              execution_1_7_rate: '',
              expected_full_year: '',
              expected_full_year_rate: '',
              sort_order: 1,
            },
            {
              fund_type: '财政专户管理资金',
              initial_budget: '',
              adjusted_budget: '',
              execution_1_7: '',
              execution_1_7_rate: '',
              expected_full_year: '',
              expected_full_year_rate: '',
              sort_order: 2,
            },
            {
              fund_type: '单位资金',
              initial_budget: '',
              adjusted_budget: '',
              execution_1_7: '',
              execution_1_7_rate: '',
              expected_full_year: '',
              expected_full_year_rate: '',
              sort_order: 3,
            },
          ];
          setFundExecutions(defaultExecutions);
        } else {
          setFundExecutions(response.data.items);
        }
      }
    } catch (e) {
      console.error('获取资金执行情况失败', e);
      // 如果API不存在，初始化默认数据
      const defaultExecutions: FundExecution[] = [
        {
          fund_type: '年度资金总额',
          initial_budget: '',
          adjusted_budget: '',
          execution_1_7: '',
          execution_1_7_rate: '',
          expected_full_year: '',
          expected_full_year_rate: '',
          sort_order: 0,
        },
        {
          fund_type: '其中:财政拨款(政府预算资金)',
          initial_budget: '',
          adjusted_budget: '',
          execution_1_7: '',
          execution_1_7_rate: '',
          expected_full_year: '',
          expected_full_year_rate: '',
          sort_order: 1,
        },
        {
          fund_type: '财政专户管理资金',
          initial_budget: '',
          adjusted_budget: '',
          execution_1_7: '',
          execution_1_7_rate: '',
          expected_full_year: '',
          expected_full_year_rate: '',
          sort_order: 2,
        },
        {
          fund_type: '单位资金',
          initial_budget: '',
          adjusted_budget: '',
          execution_1_7: '',
          execution_1_7_rate: '',
          expected_full_year: '',
          expected_full_year_rate: '',
          sort_order: 3,
        },
      ];
      setFundExecutions(defaultExecutions);
    }
  }, [projectId]);

  const handleSaveMeta = async () => {
    if (!projectId) return;
    setSavingMeta(true);
    try {
      // 保存基本信息到 performance-meta
      const metaPayload = {
        department_name: meta.department_name,
        unit_name: meta.unit_name,
        annual_target: meta.annual_target,
      };
      const resp = await apiClient.put(`/projects/${projectId}/performance-meta`, metaPayload);
      if (!resp.success) throw new Error(resp.message || '保存基本信息失败');
      
      // 保存资金执行情况到新表
      // 计算执行率
      const executionsToSave = fundExecutions.map(exec => {
        const initialBudget = parseFloat(exec.initial_budget || '0');
        const execution17 = parseFloat(exec.execution_1_7 || '0');
        const expectedFullYear = parseFloat(exec.expected_full_year || '0');
        
        const execution17Rate = initialBudget > 0 ? ((execution17 / initialBudget) * 100).toFixed(2) + '%' : '';
        const expectedFullYearRate = initialBudget > 0 ? ((expectedFullYear / initialBudget) * 100).toFixed(2) + '%' : '';
        
        return {
          ...exec,
          execution_1_7_rate: execution17Rate,
          expected_full_year_rate: expectedFullYearRate,
        };
      });
      
      const fundResp = await apiClient.post(`/projects/${projectId}/fund-executions/batch-update`, executionsToSave);
      if (!fundResp.success) throw new Error(fundResp.message || '保存资金执行情况失败');
      
      // 重新获取数据以更新ID
      await fetchFundExecutions();
    } catch (e: any) {
      setError(e.message || '保存失败');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleExtractMeta = async () => {
    if (!projectId) return;
    setExtractingMeta(true);
    try {
      const resp = await apiClient.post(`/projects/${projectId}/monitoring-indicators/extract-meta`, {});
      if (resp.success && resp.data) {
        // 刷新资金执行情况列表
        if (resp.data.fund_executions) {
          await fetchFundExecutions();
          console.log('✅ 资金执行情况已更新');
        }
      } else if (!resp.success) {
        setError(resp.message || '抽取失败');
      }
    } catch (e: any) {
      setError(e.message || '抽取失败');
    } finally {
      setExtractingMeta(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchProject();
    fetchProjectMeta();
    fetchFundExecutions();
    fetchIndicators();
  }, [fetchProject, fetchProjectMeta, fetchFundExecutions, fetchIndicators]);

  // 一级指标排序顺序
  const getLevel1Order = (level1Name: string) => {
    const order: Record<string, number> = {
      '资金管理': 1,
      '进度管理': 2,
      '质量管理': 3,
      '风险管理': 4,
      '绩效管理': 5,
    };
    return order[level1Name] || 999;
  };

  // 获取一级指标列表（去重）
  const level1Options = useMemo(() => {
    const level1Names = Array.from(new Set(FIXED_MONITORING_INDICATORS.map(item => item.level1_name)));
    return level1Names.sort((a, b) => getLevel1Order(a) - getLevel1Order(b));
  }, []);

  // 根据一级指标获取二级指标列表
  const getLevel2Options = (level1Name: string) => {
    return FIXED_MONITORING_INDICATORS
      .filter(item => item.level1_name === level1Name)
      .map(item => ({
        level2_name: item.level2_name,
        value_description: item.value_description
      }));
  };

  // 根据一级和二级指标获取指标说明
  const getValueDescription = (level1Name: string, level2Name: string) => {
    const item = FIXED_MONITORING_INDICATORS.find(
      item => item.level1_name === level1Name && item.level2_name === level2Name
    );
    return item?.value_description || '';
  };

  // 获取排序后的指标列表
  const sortedIndicators = useMemo(() => {
    return [...indicators]
      // 先按三级编码排序（核心需求：按三级指标排序）
      .sort((a, b) => (a.level3_code || '').localeCompare(b.level3_code || ''))
      // 再保证一级指标的大类顺序不变
      .sort((a, b) => {
        const orderA = getLevel1Order(a.level1_name);
        const orderB = getLevel1Order(b.level1_name);
        return orderA - orderB;
      });
  }, [indicators]);

  // 处理一级指标选择
  const handleLevel1Change = (level1Name: string) => {
    setFormData({
      ...formData,
      level1_name: level1Name,
      level1_code: '', // 由后端生成
      level2_name: '', // 重置二级指标
      level2_code: '', // 由后端生成
      value_description: '', // 重置指标说明
    });
  };

  // 处理二级指标选择
  const handleLevel2Change = (level2Name: string) => {
    const description = getValueDescription(formData.level1_name, level2Name);
    setFormData({
      ...formData,
      level2_name: level2Name,
      level2_code: '', // 由后端生成
      value_description: description, // 自动填充指标说明
    });
  };

  // 处理添加指标
  const handleAddIndicator = async () => {
    if (!projectId) return;
    
    try {
      // 不传 code 字段，由后端自动生成
      const payload = {
        level1_name: formData.level1_name,
        level2_name: formData.level2_name,
        level3_name: formData.level3_name,
        indicator_value: formData.indicator_value,
        value_description: formData.value_description,
        execution_1_7: formData.execution_1_7,
        expected_full_year: formData.expected_full_year,
        deviation_reason: formData.deviation_reason,
        reason_description: formData.reason_description,
        completion_probability: formData.completion_probability,
        note: formData.note,
      };
      
      const response = await apiClient.post(`/projects/${projectId}/monitoring-indicators`, payload);
      
      if (response.success) {
        setAddDialogOpen(false);
        resetForm();
        fetchIndicators();
      } else {
        setError(response.message || '创建事中监控指标失败');
      }
    } catch (err: any) {
      console.error('创建事中监控指标失败:', err);
      setError(err.response?.data?.detail || '创建事中监控指标失败');
    }
  };

  // 处理编辑指标
  const handleEditIndicator = async () => {
    if (!projectId || !editingIndicator) return;
    
    try {
      // 不传 code 字段，由后端自动生成
      const payload = {
        level1_name: formData.level1_name,
        level2_name: formData.level2_name,
        level3_name: formData.level3_name,
        indicator_value: formData.indicator_value,
        value_description: formData.value_description,
        execution_1_7: formData.execution_1_7,
        expected_full_year: formData.expected_full_year,
        deviation_reason: formData.deviation_reason,
        reason_description: formData.reason_description,
        completion_probability: formData.completion_probability,
        note: formData.note,
      };
      
      const response = await apiClient.put(
        `/projects/${projectId}/monitoring-indicators/${editingIndicator.id}`,
        payload
      );
      
      if (response.success) {
        setEditDialogOpen(false);
        setEditingIndicator(null);
        resetForm();
        fetchIndicators();
      } else {
        setError(response.message || '更新事中监控指标失败');
      }
    } catch (err: any) {
      console.error('更新事中监控指标失败:', err);
      setError(err.response?.data?.detail || '更新事中监控指标失败');
    }
  };

  // 处理删除指标
  const handleDeleteIndicator = async () => {
    if (!projectId || !deletingIndicator) return;
    
    try {
      const response = await apiClient.delete(
        `/projects/${projectId}/monitoring-indicators/${deletingIndicator.id}`
      );
      
      if (response.success) {
        setDeleteDialogOpen(false);
        setDeletingIndicator(null);
        fetchIndicators();
      } else {
        setError(response.message || '删除事中监控指标失败');
      }
    } catch (err: any) {
      console.error('删除事中监控指标失败:', err);
      setError(err.response?.data?.detail || '删除事中监控指标失败');
    }
  };

  // 重置表单
  const resetForm = () => {
    setFormData({
      level1_code: '',
      level1_name: '',
      level2_code: '',
      level2_name: '',
      level3_code: '',
      level3_name: '',
      indicator_value: '',
      value_description: '',
      execution_1_7: '',
      expected_full_year: '',
      deviation_reason: '',
      reason_description: '',
      completion_probability: '',
      note: '',
      ai_recognition_basis: '',
    });
  };

  // 打开编辑对话框
  const openEditDialog = (indicator: MonitoringIndicator) => {
    setEditingIndicator(indicator);
    setFormData({
      level1_code: indicator.level1_code,
      level1_name: indicator.level1_name,
      level2_code: indicator.level2_code,
      level2_name: indicator.level2_name,
      level3_code: indicator.level3_code,
      level3_name: indicator.level3_name,
      indicator_value: indicator.indicator_value,
      value_description: indicator.value_description,
      execution_1_7: indicator.execution_1_7,
      expected_full_year: indicator.expected_full_year,
      deviation_reason: indicator.deviation_reason,
      reason_description: indicator.reason_description,
      completion_probability: indicator.completion_probability,
      note: indicator.note,
      ai_recognition_basis: indicator.ai_recognition_basis || '',
    });
    setEditDialogOpen(true);
  };

  // 打开删除对话框
  const openDeleteDialog = (indicator: MonitoringIndicator) => {
    setDeletingIndicator(indicator);
    setDeleteDialogOpen(true);
  };

  // 选择单个指标进行批量删除
  const handleSelectIndicatorForBatchDelete = (indicatorId: number, checked: boolean) => {
    if (checked) {
      setSelectedIndicatorIds(prev => [...prev, indicatorId]);
    } else {
      setSelectedIndicatorIds(prev => prev.filter(id => id !== indicatorId));
    }
  };

  // 全选/取消全选指标进行批量删除
  const handleSelectAllIndicatorsForBatchDelete = (checked: boolean) => {
    if (checked) {
      setSelectedIndicatorIds(indicators.map(indicator => indicator.id));
    } else {
      setSelectedIndicatorIds([]);
    }
  };

  // 打开批量删除确认对话框
  const openBatchDeleteConfirmDialog = () => {
    if (selectedIndicatorIds.length === 0) return;
    setBatchDeleteConfirmOpen(true);
  };

  // 导出为Excel
  const handleExportExcel = async () => {
    if (!projectId) return;
    
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/monitoring-indicators/export-excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || err.error || '导出失败');
      }
      
      // 从响应头中提取文件名
      const contentDisposition = resp.headers.get('Content-Disposition') || '';
      // 使用项目名称作为默认文件名，如果没有则使用"项目"
      const projectName = project?.project_name || '项目';
      let filename = `${projectName}_项目支出绩效监控情况表.xlsx`; // 默认文件名
      
      // 优先使用后端返回的文件名
      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      } else {
        // 备用方案：尝试匹配普通filename
        const filenameMatch2 = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch2 && filenameMatch2[1]) {
          filename = filenameMatch2[1];
        }
      }
      
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  // 导出为Word
  const handleExportWord = async () => {
    if (!projectId) return;
    
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/monitoring-indicators/export-word`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || err.error || '导出失败');
      }
      
      // 从响应头中提取文件名
      const contentDisposition = resp.headers.get('Content-Disposition') || '';
      // 使用项目名称作为默认文件名，如果没有则使用"项目"
      const projectName = project?.project_name || '项目';
      let filename = `${projectName}_项目支出绩效监控情况表.docx`; // 默认文件名
      
      // 优先使用后端返回的文件名
      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      } else {
        // 备用方案：尝试匹配普通filename
        const filenameMatch2 = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch2 && filenameMatch2[1]) {
          filename = filenameMatch2[1];
        }
      }
      
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  // 打开导出菜单
  const openExportMenu = (e: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(e.currentTarget);
  };

  // 关闭导出菜单
  const closeExportMenu = () => {
    setExportMenuAnchor(null);
  };

  // 批量删除确认
  const handleConfirmBatchDelete = async () => {
    if (selectedIndicatorIds.length === 0) return;
    
    setIsBatchDeleting(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/projects/${projectId}/monitoring-indicators/batch-delete`, {
        indicator_ids: selectedIndicatorIds
      });

      if (response.success) {
        setSelectedIndicatorIds([]);
        fetchIndicators(); // 刷新列表
        setBatchDeleteConfirmOpen(false);
      } else {
        throw new Error(response.error || '批量删除失败');
      }
    } catch (err: any) {
      console.error("Error batch deleting monitoring indicators:", err);
      setError(err.message || '批量删除失败');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  // 打开排序对话框
  const handleOpenSortDialog = () => {
    setSortDialogOpen(true);
  };

  // 关闭排序对话框
  const handleCloseSortDialog = () => {
    setSortDialogOpen(false);
  };

  // 保存排序
  const handleSaveSort = async (newCodes: any[]) => {
    setIsSorting(true);
    try {
      const response = await apiClient.post(`/projects/${projectId}/monitoring-indicators/reorder`, {
        newCodes
      });

      if (response.success) {
        setSortDialogOpen(false);
        // 刷新指标列表
        fetchIndicators();
      } else {
        setError(response.message || '排序保存失败');
      }
    } catch (err: any) {
      console.error('排序保存失败:', err);
      setError(err.response?.data?.detail || '排序保存失败');
    } finally {
      setIsSorting(false);
    }
  };

  // 一键生成执行情况
  const handleGenerateExecution = async () => {
    if (!projectId) return;
    
    if (indicators.length === 0) {
      setError('没有指标数据，无法生成执行情况');
      return;
    }
    
    setIsGeneratingExecution(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/projects/${projectId}/monitoring-indicators/generate-execution`, {});
      
      if (response.success) {
        // 刷新指标列表
        await fetchIndicators();
        // 显示成功消息
        const updatedCount = response.data?.updated_count || 0;
        setError(null);
        // 可以使用成功提示，这里暂时使用控制台输出
        console.log(`✅ 成功生成 ${updatedCount} 个指标的执行情况`);
      } else {
        setError(response.message || '生成执行情况失败');
      }
    } catch (err: any) {
      console.error('生成执行情况失败:', err);
      setError(err.response?.data?.detail || '生成执行情况失败');
    } finally {
      setIsGeneratingExecution(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* 标题和操作栏 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            事中监控指标管理
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => { setAddDialogOpen(true); setDialogError(null); }}
            >
              添加指标
            </Button>
            {/* AI推荐指标按钮已隐藏 */}
            {/* {indicators.length === 0 && (
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setAiDialogOpen(true)}
              >
                AI推荐指标
              </Button>
            )} */}
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={handleOpenSortDialog}
              disabled={isSorting}
            >
              指标排序
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={isGeneratingExecution ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              onClick={handleGenerateExecution}
              disabled={isGeneratingExecution || indicators.length === 0}
            >
              {isGeneratingExecution ? '生成中...' : '一键生成执行'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={() => setImportDialogOpen(true)}
            >
              导入
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={openExportMenu}
              disabled={isExporting}
            >
              导出
            </Button>
            <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={closeExportMenu}>
              <MenuItem onClick={() => { closeExportMenu(); handleExportExcel(); }} disabled={isExporting}>
                导出为 Excel (.xlsx)
              </MenuItem>
              <MenuItem onClick={() => { closeExportMenu(); handleExportWord(); }} disabled={isExporting}>
                导出为 Word (.docx)
              </MenuItem>
            </Menu>
            {selectedIndicatorIds.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                onClick={openBatchDeleteConfirmDialog}
                disabled={isBatchDeleting}
              >
                批量删除 ({selectedIndicatorIds.length})
              </Button>
            )}
          </Stack>
        </Box>

        {/* 加载状态 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* 错误提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 顶部：项目绩效元信息（可编辑） */}
        <Paper sx={{ p: 1.5, mb: 2 }} variant="outlined">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>项目信息</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleExtractMeta} disabled={extractingMeta}>
                {extractingMeta ? '提取中...' : '提取项目信息'}
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveMeta} disabled={savingMeta}>
                {savingMeta ? '保存中...' : '保存项目信息'}
              </Button>
            </Box>
          </Box>
          
          {/* 基本信息 */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="部门名称" fullWidth size="small" value={meta.department_name}
                onChange={(e) => setMeta({ ...meta, department_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="单位名称" fullWidth size="small" value={meta.unit_name}
                onChange={(e) => setMeta({ ...meta, unit_name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="年度目标" fullWidth multiline rows={2} size="small" value={meta.annual_target}
                onChange={(e) => setMeta({ ...meta, annual_target: e.target.value })} />
            </Grid>
          </Grid>

          {/* 项目资金执行情况表格 */}
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>项目资金(万元)</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: 200 }}>项目资金</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>年初预算数</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>调整预算数</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>1-7月执行数</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>1-7月执行率</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>全年预计执行数</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>全年预计执行率</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundExecutions.map((exec, index) => {
                  // 计算执行率
                  const initialBudget = parseFloat(exec.initial_budget || '0');
                  const execution17 = parseFloat(exec.execution_1_7 || '0');
                  const expectedFullYear = parseFloat(exec.expected_full_year || '0');
                  
                  const execution17Rate = initialBudget > 0 ? ((execution17 / initialBudget) * 100).toFixed(2) + '%' : '-';
                  const expectedFullYearRate = initialBudget > 0 ? ((expectedFullYear / initialBudget) * 100).toFixed(2) + '%' : '-';
                  
                  return (
                    <TableRow key={index}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{exec.fund_type}</TableCell>
                      <TableCell>
                        <TextField 
                          fullWidth 
                          size="small" 
                          value={exec.initial_budget || ''}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].initial_budget = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="年初预算数"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField 
                          fullWidth 
                          size="small" 
                          value={exec.adjusted_budget || ''}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].adjusted_budget = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="调整预算数"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField 
                          fullWidth 
                          size="small" 
                          value={exec.execution_1_7 || ''}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].execution_1_7 = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="1-7月执行数"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{execution17Rate}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField 
                          fullWidth 
                          size="small" 
                          value={exec.expected_full_year || ''}
                          onChange={(e) => {
                            const updated = [...fundExecutions];
                            updated[index].expected_full_year = e.target.value;
                            setFundExecutions(updated);
                          }}
                          placeholder="全年预计执行数"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{expectedFullYearRate}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* 指标表格 */}
        {!loading && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh', overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead sx={{ backgroundColor: 'action.hover' }}>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      width: 48, 
                      minWidth: 48, 
                      maxWidth: 48,
                      padding: '8px'
                    }} 
                    padding="checkbox"
                  >
                    <Checkbox
                      checked={selectedIndicatorIds.length === indicators.length && indicators.length > 0}
                      indeterminate={selectedIndicatorIds.length > 0 && selectedIndicatorIds.length < indicators.length}
                      onChange={(e) => handleSelectAllIndicatorsForBatchDelete(e.target.checked)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 60, minWidth: 60, maxWidth: 60 }}>序号</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>一级指标</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>二级指标</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>三级指标</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>指标值</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>1-7月执行情况</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>全年预计完成情况</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>偏差原因</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>原因说明</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>完成目标可能性</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>备注</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>AI识别依据</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120, minWidth: 120, maxWidth: 120, textAlign: 'center' }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedIndicators.map((indicator, index) => (
                  <TableRow key={indicator.id} hover>
                    <TableCell 
                      padding="checkbox"
                      sx={{ 
                        width: 48, 
                        minWidth: 48, 
                        maxWidth: 48,
                        padding: '8px'
                      }}
                    >
                      <Checkbox
                        checked={selectedIndicatorIds.includes(indicator.id)}
                        onChange={(e) => handleSelectIndicatorForBatchDelete(indicator.id, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 60, minWidth: 60, maxWidth: 60 }}>{index + 1}</TableCell>
                    <TableCell>
                      {indicator.level1_name}
                    </TableCell>
                    <TableCell>
                      {indicator.level2_name}
                    </TableCell>
                    <TableCell>
                      {indicator.level3_name || ''}
                    </TableCell>
                    <TableCell>{indicator.indicator_value}</TableCell>
                    <TableCell>{indicator.execution_1_7}</TableCell>
                    <TableCell>{indicator.expected_full_year}</TableCell>
                    <TableCell>{indicator.deviation_reason}</TableCell>
                    <TableCell>{indicator.reason_description}</TableCell>
                    <TableCell>{indicator.completion_probability}</TableCell>
                    <TableCell>{indicator.note}</TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip 
                        title={
                          <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                            {indicator.ai_recognition_basis || '-'}
                          </Typography>
                        }
                        arrow
                        placement="top"
                        enterDelay={300}
                        componentsProps={{
                          tooltip: {
                            sx: { maxWidth: 500 }
                          }
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem',
                            color: indicator.ai_recognition_basis ? 'text.primary' : 'text.secondary',
                            cursor: indicator.ai_recognition_basis ? 'help' : 'default'
                          }}
                        >
                          {indicator.ai_recognition_basis || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 120, minWidth: 120, maxWidth: 120 }}>
                      <Tooltip title="编辑">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(indicator)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(indicator)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* 空状态 */}
        {!loading && indicators.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              暂无事中监控指标数据
            </Typography>
          </Box>
        )}
      </Paper>

      {/* 添加指标对话框 */}
      <Dialog open={addDialogOpen} onClose={() => { setAddDialogOpen(false); setDialogError(null); }} maxWidth="md" fullWidth>
        <DialogTitle>添加事中监控指标</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>一级指标</InputLabel>
                <Select
                  value={formData.level1_name}
                  label="一级指标"
                  onChange={(e) => handleLevel1Change(e.target.value)}
                >
                  {level1Options.map((level1Name) => (
                    <MenuItem key={level1Name} value={level1Name}>
                      {level1Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>二级指标</InputLabel>
                <Select
                  value={formData.level2_name}
                  label="二级指标"
                  onChange={(e) => handleLevel2Change(e.target.value)}
                  disabled={!formData.level1_name}
                >
                  {getLevel2Options(formData.level1_name).map((option) => (
                    <MenuItem key={option.level2_name} value={option.level2_name}>
                      {option.level2_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="三级指标"
                fullWidth
                value={formData.level3_name}
                onChange={(e) => setFormData({ ...formData, level3_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="指标值"
                fullWidth
                value={formData.indicator_value}
                onChange={(e) => setFormData({ ...formData, indicator_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="指标说明"
                fullWidth
                multiline
                rows={2}
                value={formData.value_description}
                onChange={(e) => setFormData({ ...formData, value_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="1-7月执行情况"
                fullWidth
                value={formData.execution_1_7}
                onChange={(e) => setFormData({ ...formData, execution_1_7: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="全年预计完成情况"
                fullWidth
                value={formData.expected_full_year}
                onChange={(e) => setFormData({ ...formData, expected_full_year: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="偏差原因"
                fullWidth
                value={formData.deviation_reason}
                onChange={(e) => setFormData({ ...formData, deviation_reason: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="原因说明"
                fullWidth
                multiline
                rows={2}
                value={formData.reason_description}
                onChange={(e) => setFormData({ ...formData, reason_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="完成目标可能性"
                fullWidth
                value={formData.completion_probability}
                onChange={(e) => setFormData({ ...formData, completion_probability: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="备注"
                fullWidth
                multiline
                rows={2}
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); setDialogError(null); }}>
            取消
          </Button>
          <Button onClick={handleAddIndicator} variant="contained">
            添加
          </Button>
        </DialogActions>
      </Dialog>

      {/* 编辑指标对话框 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>编辑事中监控指标</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>一级指标</InputLabel>
                <Select
                  value={formData.level1_name}
                  label="一级指标"
                  onChange={(e) => handleLevel1Change(e.target.value)}
                >
                  {level1Options.map((level1Name) => (
                    <MenuItem key={level1Name} value={level1Name}>
                      {level1Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>二级指标</InputLabel>
                <Select
                  value={formData.level2_name}
                  label="二级指标"
                  onChange={(e) => handleLevel2Change(e.target.value)}
                  disabled={!formData.level1_name}
                >
                  {getLevel2Options(formData.level1_name).map((option) => (
                    <MenuItem key={option.level2_name} value={option.level2_name}>
                      {option.level2_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="三级指标"
                fullWidth
                value={formData.level3_name}
                onChange={(e) => setFormData({ ...formData, level3_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="指标值"
                fullWidth
                value={formData.indicator_value}
                onChange={(e) => setFormData({ ...formData, indicator_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="指标说明"
                fullWidth
                multiline
                rows={2}
                value={formData.value_description}
                onChange={(e) => setFormData({ ...formData, value_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="1-7月执行情况"
                fullWidth
                value={formData.execution_1_7}
                onChange={(e) => setFormData({ ...formData, execution_1_7: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="全年预计完成情况"
                fullWidth
                value={formData.expected_full_year}
                onChange={(e) => setFormData({ ...formData, expected_full_year: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="偏差原因"
                fullWidth
                value={formData.deviation_reason}
                onChange={(e) => setFormData({ ...formData, deviation_reason: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="原因说明"
                fullWidth
                multiline
                rows={2}
                value={formData.reason_description}
                onChange={(e) => setFormData({ ...formData, reason_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="完成目标可能性"
                fullWidth
                value={formData.completion_probability}
                onChange={(e) => setFormData({ ...formData, completion_probability: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="备注"
                fullWidth
                multiline
                rows={2}
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            取消
          </Button>
          <Button onClick={handleEditIndicator} variant="contained">
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除此指标吗？此操作不可恢复。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            取消
          </Button>
          <Button onClick={handleDeleteIndicator} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批量删除确认对话框 */}
      <Dialog open={batchDeleteConfirmOpen} onClose={() => setBatchDeleteConfirmOpen(false)}>
        <DialogTitle>确认批量删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除选中的 {selectedIndicatorIds.length} 个指标吗？此操作不可恢复。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteConfirmOpen(false)} disabled={isBatchDeleting}>
            取消
          </Button>
          <Button onClick={handleConfirmBatchDelete} color="error" variant="contained" disabled={isBatchDeleting}>
            {isBatchDeleting ? '删除中...' : '确认删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 排序对话框 */}
      <MonitoringIndicatorSortDialog
        open={sortDialogOpen}
        onClose={handleCloseSortDialog}
        indicators={indicators}
        onSave={handleSaveSort}
        isSaving={isSorting}
      />

      {/* 导入对话框 */}
      <MonitoringIndicatorImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        projectId={projectId || ''}
        onImportSuccess={() => {
          fetchIndicators(); // 刷新指标列表
          fetchProjectMeta(); // 刷新基本信息
          fetchFundExecutions(); // 刷新资金执行情况
        }}
      />
    </Box>
  );
};

export default MonitoringIndicatorManager;